import * as React from 'react';
import * as _ from 'lodash';
import {useState, useEffect, useCallback} from 'react';
import {Placeholder, Icon, Popup, Modal} from 'semantic-ui-react';

import {PartRefFromObjSchema} from '../state/views/types';
import * as PanelTypes from '../state/views/panel/types';
import {loadBoxData, clearBoxData} from '../state/media/actions';

import {
  ImageMetadata,
  BoundingBox2D,
  BoundingBoxFileData,
} from '../types/media';
import {RunHistoryRow, RunSignature} from '../types/run';
import {
  useStepWithRollback,
  mediaFilename,
  mediaSrc,
  runFileSource,
  labelComponent,
  getMasks,
  getBoundingBoxes,
  makeCaptions,
  WBFile,
  WANDB_MASK_CLASS_LABEL_KEY,
  WANDB_BBOX_CLASS_LABEL_KEY,
  WANDB_DELIMETER,
  getSingletonValue,
  segmentationMaskColor,
  boxColor,
  DEFAULT_ALL_MASK_CONTROL,
  DEFAULT_CLASS_MASK_CONTROL,
} from '../util/media';
import {runLink} from '../util/runhelpers';
import {Media} from '../util/runs';
import {compare} from '../util/ops';
import {apolloClient} from '../setup';
import MessageMediaNotFound from './MessageMediaNotFound';

import memoize from 'memoize-one';
import {
  useLoadFile,
  downloadDataUrl,
  fetchFileInfo,
  FileInfo,
} from '../util/requests';
import {
  MediaCardProps,
  MaskControl,
  BoundingBoxClassControl,
  BoundingBoxSliderControl,
  Style,
  LineStyle,
} from './MediaCard';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {toast} from './elements/Toast';
import {captureError} from '../util/integrations';
import {useDispatch} from 'react-redux';
import makeComp from '../util/profiler';

interface ImageInfo {
  imgSrc: string;
  imgFile: string;
  type: 'single' | 'sprite';
}

function imgPaths(
  media: ImageMetadata | null,
  runSignature: RunSignature,
  mediaKey: string,
  step: number,
  index: number
): ImageInfo {
  let imgSrc: string;
  let imgFile: string;
  let type: 'single' | 'sprite';
  if (media && media.path) {
    imgSrc = runFileSource(runSignature, media.path);
    imgFile = media.path;
    type = 'single';
  } else if (media?._type === 'images/separated') {
    // This is the new format for a collection images which
    // is a set of individual images, not a sprite
    imgSrc = mediaSrc(
      runSignature,
      [mediaKey, step, index] as string[],
      'images',
      media ? media.format : 'jpg'
    );

    imgFile = mediaFilename(
      [mediaKey, step, index] as string[],
      'images',
      media ? media.format : 'jpg'
    );

    type = 'single';
  } else {
    // Our old multiple image format expected a sprite
    // we use the mediaIndex to determine the pixel offset
    // for the image we want
    imgSrc = mediaSrc(
      runSignature,
      [mediaKey, step] as string[],
      'images',
      media ? media.format : 'jpg'
    );

    imgFile = mediaFilename(
      [mediaKey, step] as string[],
      'images',
      media ? media.format : 'jpg'
    );

    type = 'sprite';
  }
  return {type, imgSrc, imgFile};
}

type ImageCardProps = MediaCardProps;

export const ImageCard: React.FunctionComponent<ImageCardProps> = makeComp(
  props => {
    const {
      run,
      globalStep,
      width,
      height,
      mediaKey,
      mediaIndex,
      runSignature,
    } = props;

    // Keep an old image so we can display the last image instead of
    // loading between step updates
    const [firstLoad, setImageFirstLoad] = useState(true);
    const [imgLoaded, setImageLoaded] = useState(false);

    const onImgLoad = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setImageFirstLoad(false);
        setImageLoaded(true);
      },
      []
    );

    const {
      currentStep,
      stepData,
      failedToLoad,
      rollback,
      rollbackCount,
    } = useStepWithRollback(props);

    const media =
      currentStep != null ? getMedia(stepData, props.mediaKey) : null;

    const paths =
      currentStep != null
        ? imgPaths(
            media as ImageMetadata | null,
            runSignature,
            mediaKey,
            currentStep,
            mediaIndex
          )
        : null;

    const imgSrc = paths && paths.imgSrc;
    const imgFile = paths && paths.imgFile;

    const currentMediaMetadata = media as ImageMetadata | null;

    // Compute pixel offset for sprite based images
    //
    // This will be scaled to the card and used for display
    // to calculate to offset.
    const spritePixelOffset =
      paths && paths.type === 'sprite' ? mediaIndex * width : 0;

    // Compute the pixel offset for the original sprite
    // This is used for downloading
    const originalWidth = currentMediaMetadata && currentMediaMetadata.width;
    const originalPixelOffset =
      paths && paths.type === 'sprite' && originalWidth
        ? mediaIndex * originalWidth
        : 0;

    const imgRef = React.useRef<HTMLImageElement>(null);
    const originalSizeRef = React.useRef<HTMLImageElement>(null);

    // Create a refresh handler to refresh the dom node
    const loadSuccess = useCallback(
      (blob: Blob, {directUrl}: {directUrl: string}) => {
        // Refresh the dom element
        if (imgRef.current) {
          imgRef.current.src = directUrl;
        }
        if (originalSizeRef.current) {
          originalSizeRef.current.src = directUrl;
        }
      },
      []
    );

    const [domLoadFailed, setDomLoadFailed] = React.useState<boolean>();

    // Use the file loader, hook to poll with retries and fallback
    // on partial data
    //
    // ! Warning: The callbacks passed to useLoadFile must stay
    // equal by reference or it will trigger a new file download.
    //
    // We prefer loading files in the dom so they are cached
    // and they aren't required to make a round trip for the url.
    // Therefore this hook is skipped until a failure occurs
    useLoadFile(runSignature, imgFile || '', {
      onSuccess: loadSuccess,
      fallback: rollback,
      skip: !domLoadFailed || !imgFile || failedToLoad,
      responseType: 'blob',
    });

    const masksRef = React.useRef<HTMLDivElement>(null);
    const boxesRef = React.useRef<HTMLDivElement>(null);

    const maskData = React.useMemo(() => {
      const masks =
        currentMediaMetadata != null &&
        getMasks(currentMediaMetadata, props.mediaIndex);
      return (
        masks &&
        Object.keys(masks).map(key => {
          const mask = masks[key];
          return {
            mediaKey: props.mediaKey,
            maskKey: key,
            mask,
            classLabels: getSingletonValue(
              run,
              WANDB_MASK_CLASS_LABEL_KEY,
              props.mediaKey + WANDB_DELIMETER + key
            ) as {[key: number]: string},
          };
        })
      );
    }, [currentMediaMetadata, props.mediaIndex, props.mediaKey, run]);

    const boundingBoxData = React.useMemo(() => {
      const boxes =
        currentMediaMetadata != null
          ? getBoundingBoxes(currentMediaMetadata, props.mediaIndex)
          : {};

      return Object.keys(boxes).map(key => {
        const boxFileInfo = boxes[key];
        return {
          mediaKey: props.mediaKey,
          boxKey: key,
          boxFileInfo,
          classLabels: getSingletonValue(
            run,
            WANDB_BBOX_CLASS_LABEL_KEY,
            props.mediaKey + WANDB_DELIMETER + key
          ),
        };
      });
    }, [currentMediaMetadata, props.mediaIndex, props.mediaKey, run]);

    const mediaGroupingCount =
      (currentMediaMetadata && currentMediaMetadata.grouping) || 1;

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    const showMetadataInline = width > 100; // if this is false, metadata will appear in a popup on the image

    // get captions for all images in the group
    const captions = currentMediaMetadata
      ? makeCaptions(currentMediaMetadata, mediaIndex, mediaGroupingCount)
      : [];

    if (currentStep == null || imgSrc == null || failedToLoad) {
      return (
        <div style={{height: '100%'}}>
          <div className="image-card" style={{width, height, display: 'flex'}}>
            <MessageMediaNotFound
              basic
              mediaIndex={mediaIndex}
              mediaKey={mediaKey}
              stepIndex={currentStep || globalStep}
              mediaType="images"
            />
          </div>
        </div>
      );
    }

    const mediaSize = props.mediaWidth &&
      props.mediaHeight && {width: props.mediaWidth, height: props.mediaHeight};

    const showImage = props.maskOptions?.showImage === true;
    const imageDisplay = showImage ? 'initial' : 'none';

    const renderCard = (
      <div className="image-card content-card" style={{width}}>
        {labelComponent(props, currentStep, titleLink)}
        <div className="image-card-image" style={{width, height}}>
          {firstLoad && !imgLoaded && (
            <Placeholder style={{width, height, maxWidth: width}}>
              <Placeholder.Image />
            </Placeholder>
          )}
          <img
            data-test="image-card-img"
            ref={imgRef}
            src={imgSrc}
            onLoad={onImgLoad}
            onError={() => {
              setDomLoadFailed(true);
            }}
            alt="card"
            style={{
              left: props.actualSize
                ? -originalPixelOffset
                : -spritePixelOffset,
              height: '100%',
              display: imageDisplay,
            }}
          />

          {rollbackCount > 0 && (
            <div className={'content-card__fallback'}>
              <Popup
                trigger={
                  <Icon
                    style={{color: 'white'}}
                    size="small"
                    name="exclamation circle"
                  />
                }>
                No media was found for step: {props.globalStep}
                <br />
                The most recent step: {currentStep} is being displayed
              </Popup>
            </div>
          )}

          {imgFile && originalWidth && (
            <LegacyWBIcon
              className="content-card__download"
              onClick={() => {
                downloadSpritePart(
                  runSignature,
                  imgFile,
                  originalPixelOffset,
                  originalWidth,
                  {boxesRef, masksRef}
                );
              }}
              name="download"
            />
          )}

          {mediaSize && maskData && (
            <div ref={masksRef} className="segmentation-mask__container">
              {maskData.map(({maskKey, mask, classLabels}) => {
                return (
                  _.includes(props.maskOptions?.maskKeys ?? [], maskKey) && (
                    <SegmentationMask
                      style={{position: 'absolute', top: 0}}
                      key={maskKey}
                      mediaKey={mediaKey}
                      maskControls={_.get(
                        props.controls?.segmentationMaskControl?.toggles,
                        [mediaKey, maskKey]
                      )}
                      maskKey={maskKey}
                      classLabels={classLabels}
                      runSignature={runSignature}
                      mask={mask}
                      cardSize={{width, height}}
                      mediaSize={mediaSize}
                      mediaPanelRef={props.mediaPanelRef}
                    />
                  )
                );
              })}
            </div>
          )}
          {mediaSize && boundingBoxData && (
            <div ref={masksRef} className="bounding-boxes__container">
              {boundingBoxData.map(({boxKey, boxFileInfo, classLabels}) => (
                <BoundingBoxes
                  style={{position: 'absolute', top: 0}}
                  key={boxKey}
                  mediaKey={mediaKey}
                  boxStyle={
                    props.controls?.boundingBoxControl?.styles?.[mediaKey]?.[
                      boxKey
                    ]
                  }
                  boxToggles={_.get(
                    props.controls?.boundingBoxControl?.toggles,
                    [mediaKey, boxKey],
                    undefined
                  )}
                  boxSliders={props.controls?.boundingBoxControl?.sliders}
                  boxKey={boxKey}
                  classLabels={classLabels}
                  runSignature={runSignature}
                  boxFileInfo={boxFileInfo}
                  cardSize={{width, height}}
                  mediaSize={mediaSize}
                  mediaPanelRef={props.mediaPanelRef}
                />
              ))}
            </div>
          )}
        </div>
        {/* CAPTIONS */}
        {showMetadataInline && captions.length > 0 && (
          <div className="image-card-caption">{captions}</div>
        )}
      </div>
    );

    return !showImage || imgSrc == null ? (
      renderCard
    ) : (
      // Click image to show original size in modal
      <Modal
        size="fullscreen"
        trigger={renderCard}
        content={
          <div
            style={{
              margin: 12,
              background: 'white',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <div
              style={{
                display: 'flex',
                width: currentMediaMetadata?.width ?? props.mediaWidth,
                height: currentMediaMetadata?.height ?? props.mediaHeight,
                overflow: 'auto',
              }}>
              <img
                style={{marginLeft: -originalPixelOffset}}
                alt="card"
                ref={originalSizeRef}
                src={imgSrc}
              />
            </div>
          </div>
        }
      />
    );
  },
  {id: 'ImageCard'}
);

// Get the new-style Media object or null if there isn't one.
const getMedia = memoize(
  (stepData: RunHistoryRow | undefined, mediaKey: string): Media | null => {
    if (stepData == null || stepData[mediaKey] == null) {
      return null;
    }
    const stepMedia = stepData[mediaKey] as Media;
    if (
      stepMedia._type === 'images' ||
      stepMedia._type === 'image-file' ||
      stepMedia._type === 'images/separated'
    ) {
      return stepMedia;
    } else {
      return null;
    }
  }
);

function downloadSpritePart(
  runSignature: RunSignature,
  filename: string,
  offset: number,
  width: number,
  opts?: {
    boxesRef?: React.RefObject<HTMLDivElement>;
    masksRef?: React.RefObject<HTMLDivElement>;
  }
) {
  // We must create a new Image element instead of using the one already in
  // the dom because the one in the dom will throw a CORS TaintedCanvas
  // Error when converted to a dataUrl
  const img = new Image();

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;

  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const height = img.height;

    canvas.width = width;
    canvas.height = height;

    context.drawImage(img, offset, 0, width, height, 0, 0, width, height);

    // If we have masks render them on the download
    if (opts?.masksRef?.current) {
      const allMaskCanvases = opts.masksRef.current.querySelectorAll('canvas');

      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < allMaskCanvases.length; i++) {
        const maskCanvas = allMaskCanvases[i];
        context.drawImage(maskCanvas, 0, 0);
      }
    }

    // If we have boxes render them on the download
    if (opts?.boxesRef?.current) {
      const allBoxCanvases = opts.boxesRef.current.querySelectorAll('canvas');

      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < allBoxCanvases.length; i++) {
        const boxCanvas = allBoxCanvases[i];
        context.drawImage(boxCanvas, 0, 0);
      }
    }

    const dataUrl = canvas.toDataURL();

    downloadDataUrl(dataUrl, filename);
  };

  fetchFileInfo(apolloClient, {...runSignature, filename})
    .then(file => {
      if (file?.directUrl) {
        img.src = file?.directUrl;
      }
    })
    .catch(e => {
      console.error(e);
      captureError(e, 'Image Card Download');
      toast('File Download Failed');
    });

  return;
}

const BoundingBoxes = makeComp(
  (props: {
    style?: React.CSSProperties;
    classLabels: {[key: number]: string} | undefined;
    mediaKey: string;
    boxKey: string;
    boxFileInfo: WBFile;
    runSignature: RunSignature;
    mediaSize: {
      width: number;
      height: number;
    };
    cardSize: {
      width: number;
      height: number;
    };
    mediaPanelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
    boxStyle?: Style;
    boxToggles?: {[classOrAll: string]: BoundingBoxClassControl};
    boxSliders?: {[sliderKey: string]: BoundingBoxSliderControl};
  }) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [boxData, setBoxData] = React.useState<BoundingBoxFileData>();
    const [fileMetadata, setFileMetadata] = React.useState<FileInfo>();
    useEffect(() => {
      if (canvasRef.current && boxData != null) {
        clearCanvas(canvasRef.current);
        renderBoxes(canvasRef.current, boxData, {
          cardSize: props.cardSize,
          mediaSize: props.mediaSize,
          boxStyle: props.boxStyle,
          boxToggles: props.boxToggles,
          boxSliders: props.boxSliders,
        });
      }
    }, [
      props.cardSize,
      props.mediaSize,
      boxData,
      props.boxSliders,
      props.boxToggles,
      props.boxStyle,
    ]);

    const loadSuccess = useCallback((b: BoundingBoxFileData, m: FileInfo) => {
      setBoxData(b);
      setFileMetadata(m);
    }, []);

    useLoadFile(props.runSignature, props.boxFileInfo.path, {
      onSuccess: loadSuccess,
      responseType: 'json',
    });

    const dispatch = useDispatch();

    useEffect(() => {
      if (fileMetadata != null && boxData != null) {
        dispatch(
          loadBoxData({
            mediaKey: props.mediaKey,
            panelID: props.mediaPanelRef.id,
            // Use the file ID as the identifier instead of
            // combining mediaKey, boxKey, step, index, run.id
            mediaID: fileMetadata.id,
            boxData: boxData.box_data,
          })
        );
      }
    }, [
      boxData,
      fileMetadata,
      props.mediaPanelRef.id,
      props.mediaKey,
      dispatch,
    ]);

    // Cleanup redux state
    useEffect(() => {
      return () => {
        if (fileMetadata != null && boxData != null) {
          dispatch(
            clearBoxData({
              mediaKey: props.mediaKey,
              panelID: props.mediaPanelRef.id,
              mediaID: fileMetadata.id,
            })
          );
        }
      };
    }, [
      fileMetadata,
      props.mediaKey,
      props.mediaPanelRef.id,
      boxData,
      dispatch,
    ]);

    return (
      <div
        key={props.boxKey}
        style={{
          width: props.cardSize.width,
          height: props.cardSize.height,
          ...props.style,
        }}>
        {/* TODO: Don't make the canvas scale make the drawing scale */}
        <canvas
          width={props.mediaSize.width}
          height={props.mediaSize.height}
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
          }}
          className="segmentation-mask__mask"></canvas>
      </div>
    );
  },
  {id: 'BoundingBoxes'}
);

const SegmentationMask = makeComp(
  (props: {
    style?: React.CSSProperties;
    classLabels: {[key: number]: string} | undefined;
    mediaKey: string;
    maskKey: string;
    mask: WBFile;
    runSignature: RunSignature;
    mediaSize: {
      width: number;
      height: number;
    };
    cardSize: {
      width: number;
      height: number;
    };
    mediaPanelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
    maskControls?: {[classOrAll: string]: MaskControl};
  }) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    // Tooltip is currently disabled due to poor behavior with multiple masks
    // const tooltipRef = React.useRef<HTMLDivElement>(null);

    const [classIDImageData, setClassIDImageData] = useState<ImageData>();
    const [maskImageData, setMaskImageData] = useState<ImageData>();

    // On file load pull image data into memory
    const loadSuccess = React.useCallback(
      (response: Blob, metadata: FileInfo) => {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = props.mediaSize.width;
        tempCanvas.height = props.mediaSize.height;
        const ctx = tempCanvas.getContext('2d');
        if (ctx == null) {
          throw new Error("Can't get context in Segmentation Mask");
        }

        const img = new Image(props.mediaSize.width, props.mediaSize.height);
        // Load Results into image data so we can read the values
        // in memory
        img.onload = () => {
          ctx?.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(
            0,
            0,
            props.mediaSize.width,
            props.mediaSize.height
          );
          setClassIDImageData(imageData);
        };
        img.crossOrigin = 'Anonymous';
        img.src = metadata.directUrl;
      },
      [props.mediaSize.width, props.mediaSize.height]
    );

    // Use effect compares objects by reference, so when only a subset of the object
    // changes the effect won't fire
    const maskControls = props.maskControls;

    // Transform file data into a mask image
    useEffect(() => {
      if (classIDImageData == null) {
        // Do nothing because data is not loaded
        return;
      }
      const newImageData = new ImageData(
        props.mediaSize.width,
        props.mediaSize.height
      );

      const IDtoColor = Object.keys(props.classLabels ?? {}).reduce(
        (res, k: string) => {
          const id = parseInt(k, 10);
          res[k] = segmentationMaskColor(id);
          return res;
        },
        {} as {[key: string]: [number, number, number]}
      );

      // Transform class ids to a map of colors
      for (let x = 0; x < props.mediaSize.width; x++) {
        for (let y = 0; y < props.mediaSize.height; y++) {
          const index = x * 4 + y * 4 * props.mediaSize.width;
          const classID = classIDImageData.data[index];
          const color = IDtoColor[classID];
          if (color == null) {
            continue;
          }
          const [r, g, b] = color;
          const allToggle = maskControls?.all ?? DEFAULT_ALL_MASK_CONTROL;
          const classToggle =
            maskControls?.[classID] ?? DEFAULT_CLASS_MASK_CONTROL;

          if (allToggle.disabled || classToggle.disabled) {
            continue;
          }
          const a = allToggle.opacity * classToggle.opacity * 255;

          newImageData.data[index] = r;
          newImageData.data[index + 1] = g;
          newImageData.data[index + 2] = b;
          newImageData.data[index + 3] = a;
        }
      }

      setMaskImageData(newImageData);
    }, [
      props.classLabels,
      props.mediaSize.width,
      props.mediaSize.height,
      classIDImageData,
      maskControls,
    ]);

    // Draw image data
    useEffect(() => {
      if (canvasRef.current == null || maskImageData == null) {
        return;
      }
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx == null) {
        throw new Error("Can't get context in image render");
      }
      ctx.putImageData(maskImageData, 0, 0);
    }, [maskImageData]);

    useLoadFile(props.runSignature, props.mask.path, {
      onSuccess: loadSuccess,
      responseType: 'blob',
    });

    // const [tooltipVisible, setTooltipVisible] = useState<boolean>();

    // const moveTooltip = useCallback(
    //   (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    //     const tooltipDomEle = tooltipRef.current;
    //     const canvasDomEle = canvasRef.current;

    //     if (tooltipDomEle && canvasDomEle) {
    //       const x = e.pageX - canvasDomEle.offsetLeft;
    //       const y = e.pageY - canvasDomEle.offsetTop;

    //       tooltipDomEle.style.transform = `translate3D(${x}px,${y}px,0)`;
    //     }
    //   },
    //   []
    // );

    return (
      <div
        key={props.maskKey}
        style={{
          width: props.cardSize.width,
          height: props.cardSize.height,
          ...props.style,
        }}>
        <canvas
          width={props.mediaSize.width}
          height={props.mediaSize.height}
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
          }}
          className="segmentation-mask__mask"
          // onMouseMove={e => {
          //   moveTooltip(e);
          // }}
          // onMouseOver={e => {
          //   setTooltipVisible(true);
          // }}
          // onMouseLeave={e => setTooltipVisible(false)}
        />
        {/* <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          display: tooltipVisible ? 'auto' : 'hidden',
        }}
        ref={tooltipRef}
        className="segmentation-mask__tooltip">
        Tooltip
      </div> */}
      </div>
    );
  },
  {id: 'SegmentationMask'}
);

// Helper function to check if a box should be displayed
function isHidden(
  box: BoundingBox2D,
  controls: {
    boxToggles?: {[classOrAll: string]: BoundingBoxClassControl};
    boxSliders?: {[sliderKey: string]: BoundingBoxSliderControl};
  }
) {
  if (
    _.get(controls.boxToggles, [box.class_id, 'disabled'], false) ||
    _.get(controls.boxToggles, ['all', 'disabled'], false)
  ) {
    return true;
  }
  if (controls.boxSliders == null) {
    return false;
  }
  for (const k of Object.keys(controls.boxSliders)) {
    const slider = controls.boxSliders?.[k];
    if (box.scores == null) {
      return true;
    }
    const bScore = box.scores[k];
    if (
      slider?.value != null &&
      bScore != null &&
      !(slider.disabled ?? false) &&
      !compare(slider.comparator ?? 'gte', bScore, slider.value)
    ) {
      return true;
    }
  }

  return false;
}

export const renderBoxes = (
  c: HTMLCanvasElement,
  boxData: BoundingBoxFileData,
  opts: {
    boxStyle: Style | undefined;
    boxToggles?: {[classOrAll: string]: BoundingBoxClassControl};
    boxSliders?: {[sliderKey: string]: BoundingBoxSliderControl};
    cardSize: {width: number; height: number};
    mediaSize: {width: number; height: number};
  }
) => {
  const {mediaSize} = opts;
  const ctx = c.getContext('2d');
  if (ctx == null) {
    throw new Error('Canvas context not valid');
  }
  const boxes = boxData.box_data;
  for (const box of boxes) {
    // Check if the box should be hidden based on control settings
    if (isHidden(box, opts)) {
      continue;
    }

    let w: number;
    let h: number;
    let x: number;
    let y: number;
    if ('minX' in box.position) {
      w = box.position.maxX - box.position.minX;
      h = box.position.maxY - box.position.minY;
      x = box.position.minX;
      y = box.position.minY;
    } else {
      w = box.position.width;
      h = box.position.height;
      x = box.position.middle[0] - w / 2;
      y = box.position.middle[1] - h / 2;
    }

    const domain = box.domain;
    if (domain === 'pixel') {
      // Do nothing
    } else {
      x = x * mediaSize.width;
      y = y * mediaSize.height;
      w = w * mediaSize.width;
      h = h * mediaSize.height;
    }

    const color = boxColor(box.class_id);

    // Draw the 2D Box
    const lineWidth = 3;
    ctx.lineWidth = lineWidth;
    if (opts.boxStyle?.lineStyle != null) {
      ctx.setLineDash(lineDashArray(opts.boxStyle?.lineStyle));
    }
    ctx.strokeStyle = color;
    ctx.strokeRect(x, y, w, h);

    // Draw the label
    const {box_caption} = box;
    const labelHeight = 14;
    ctx.font = '14px Arial';
    const labelPad = 4;

    const text = box_caption ?? `ID: ${box.class_id}`;
    const tm = ctx.measureText(text);
    // If label doesn't fit draw from right edge instead of left
    const labelShift = tm.width + x > c.width ? w - tm.width - labelPad : 0;
    // Label background
    ctx.fillStyle = color;
    ctx.fillRect(
      x - lineWidth / 2 + labelShift,
      y - labelHeight - 2 * labelPad,
      tm.width + labelPad * 2,
      labelHeight + 2 * labelPad
    );

    // Text
    ctx.fillStyle = 'white';
    ctx.fillText(text, x + labelPad + labelShift, y - labelPad);
  }
};

export const clearCanvas = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Tried to clear canvas without context');
  }
  ctx.clearRect(0, 0, canvas.width, canvas.height);
};

export const lineDashArray = (dashStyle: LineStyle) => {
  const mapping = {
    line: [],
    dotted: [2, 2],
    dashed: [12, 6],
  };

  return mapping[dashStyle];
};

export default ImageCard;
